import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Download, Phone } from "lucide-react";

const LOGO_URL =
  "https://customer-assets.emergentagent.com/job_wealth-freedom-nav/artifacts/wdt06ylk_SAHI%20DISHA%20FINAL-06.jpeg";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

async function downloadBlueprint(inputs, leadId) {
  const body = {
    monthly_expenses: inputs.monthlyExpenses,
    current_age: inputs.currentAge,
    freedom_age: inputs.freedomAge,
    annual_return: inputs.annualReturn,
    annual_inflation: inputs.annualInflation,
    stepup_pct: inputs.stepupPct,
    lead_id: leadId || undefined,
  };
  const res = await axios.post(`${API}/generate-pdf`, body, { responseType: "blob" });
  const url = URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
  const a = document.createElement("a");
  a.href = url;
  a.download = "Sahi-Disha-Passive-Income-Blueprint.pdf";
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

export default function Header({ onPrimaryClick, leadId, inputs, entered }) {
  const [loading, setLoading] = useState(false);

  const handleDownload = async () => {
    if (!entered) return;
    try {
      setLoading(true);
      await downloadBlueprint(inputs, leadId);
      toast.success("Your blueprint is downloading");
    } catch (_e) {
      toast.error("Could not generate PDF. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <header
      className="sticky top-0 z-40 bg-[#0A0F1C]/80 backdrop-blur-md border-b border-white/10"
      data-testid="site-header"
    >
      {/* Mobile: centered large logo only */}
      <div className="md:hidden mx-auto max-w-7xl px-4 py-4 flex items-center justify-center">
        <a href="#" className="flex items-center justify-center" data-testid="brand-logo-link-mobile">
          <img
            src={LOGO_URL}
            alt="Sahi Disha Financial Services Pvt. Ltd."
            className="h-28 w-auto max-w-[80vw] object-contain rounded-xl bg-white p-3 shadow-2xl shadow-orange-500/10 ring-1 ring-white/10"
          />
        </a>
      </div>

      {/* Desktop: 3-col grid with centered large logo */}
      <div className="hidden md:grid mx-auto max-w-7xl px-6 lg:px-8 py-5 grid-cols-3 items-center gap-3">
        <div className="flex items-center justify-start">
          <a
            href="tel:9824050200"
            className="inline-flex items-center gap-2 text-sm text-slate-300 hover:text-white transition-colors"
            data-testid="header-call-link"
          >
            <Phone className="w-4 h-4 text-[#F97316]" />
            +91 98240 50200
          </a>
        </div>

        <a href="#" className="flex items-center justify-center" data-testid="brand-logo-link">
          <img
            src={LOGO_URL}
            alt="Sahi Disha Financial Services Pvt. Ltd."
            className="h-32 lg:h-40 w-auto object-contain rounded-xl bg-white p-2 shadow-2xl shadow-orange-500/10 ring-1 ring-white/10"
          />
        </a>

        <div className="flex items-center justify-end gap-3">
          <Button
            onClick={onPrimaryClick}
            variant="outline"
            className="border-white/15 bg-transparent hover:bg-white/5 text-white"
            data-testid="header-open-calculator-btn"
          >
            Open Calculator
          </Button>
          <Button
            onClick={handleDownload}
            disabled={loading || !entered}
            className="bg-[#F97316] hover:bg-[#EA580C] text-white font-medium shadow-lg shadow-orange-500/20"
            data-testid="header-download-pdf-btn"
          >
            <Download className="w-4 h-4 mr-2" />
            {loading ? "Preparing…" : "Download PDF"}
          </Button>
        </div>
      </div>
    </header>
  );
}
